import React, { useEffect, useState, useRef, useCallback } from "react";
import { api } from "../../lib/api";
import {
  ShieldCheck, MonitorCog, KeyRound, Mail, UserCheck, Webhook, CheckCircle2,
  RefreshCw, DownloadCloud, Loader2, AlertTriangle, Clock, ChevronDown, ChevronUp, History, XCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import { fmtMDT } from "../../lib/time";

const ICONS = { ShieldCheck, MonitorCog, KeyRound, Mail, UserCheck, Webhook };

const ENTITY_LABELS = {
  controls: "Controls", frameworks: "Frameworks", policies: "Policies", risks: "Risks", evidence: "Evidence",
};

function SprintoImportCard({ isAdmin }) {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [regionInput, setRegionInput] = useState("us");
  const [savingKey, setSavingKey] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyJobs, setHistoryJobs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/compliance/integrations/sprinto/status");
      setStatus(data);
      const jobRunning = data.last_job && ["queued", "running"].includes(data.last_job.status);
      if (jobRunning && !pollRef.current) {
        pollRef.current = setInterval(loadStatus, 2500);
      } else if (!jobRunning && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadStatus]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/compliance/integrations/sprinto/jobs");
      setHistoryJobs(data.jobs || []);
      setHistoryLoaded(true);
    } catch { /* noop */ } finally { setHistoryLoading(false); }
  }, []);

  // Keep an already-open history panel fresh while a job is polling to completion
  useEffect(() => {
    if (historyOpen && status?.last_job && !["queued", "running"].includes(status.last_job.status)) {
      loadHistory();
    }
  }, [historyOpen, status?.last_job?.status, loadHistory]);

  const trigger = async (mode) => {
    setStarting(true);
    try {
      await api.post(`/compliance/integrations/sprinto/${mode}`);
      toast({ title: mode === "import" ? "Import started" : "Sync started", description: "This runs in the background — the status below will update automatically." });
      await loadStatus();
    } catch (e) {
      toast({ title: "Couldn't start import", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setStarting(false); }
  };

  const saveKey = async () => {
    if (!apiKeyInput.trim()) return;
    setSavingKey(true);
    try {
      await api.post("/compliance/integrations/sprinto/key", { api_key: apiKeyInput.trim(), region: regionInput });
      toast({ title: "Sprinto API key saved", description: "You can now click \u201cImport now\u201d to run the first sync." });
      setApiKeyInput("");
      await loadStatus();
    } catch (e) {
      toast({ title: "Couldn't save key", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSavingKey(false); }
  };

  if (loading) return <div className="h-40 rounded-xl bg-muted/40 animate-pulse mb-4" />;

  const job = status?.last_job;
  const jobRunning = job && ["queued", "running"].includes(job.status);
  const syncedBefore = !!status?.synced_before;
  const counts = status?.imported_counts || {};
  const totalImported = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="rounded-xl border border-border bg-card p-4 mb-4" data-testid="compliance-sprinto-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <DownloadCloud className="w-4.5 h-4.5 text-primary" />
          </div>
          <div>
            <div className="text-[13.5px] font-semibold">Sprinto</div>
            <div className="text-[11.5px] text-muted-foreground mt-0.5">
              Import Frameworks, Controls, Policies, Risks &amp; Evidence from your Sprinto account.
            </div>
          </div>
        </div>
        {status?.configured && syncedBefore ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 px-2 py-0.5 shrink-0" data-testid="compliance-sprinto-status-configured">
            <CheckCircle2 className="w-3 h-3" /> Connected ({status.region.toUpperCase()})
          </span>
        ) : status?.configured ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-600 px-2 py-0.5 shrink-0" data-testid="compliance-sprinto-status-configured-unsynced">
            <KeyRound className="w-3 h-3" /> Key saved, never synced
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-600 px-2 py-0.5 shrink-0" data-testid="compliance-sprinto-status-not-configured">
            <AlertTriangle className="w-3 h-3" /> Not configured
          </span>
        )}
      </div>

      {!syncedBefore && isAdmin && (
        <div className="mt-3 rounded-lg bg-muted/30 border border-border p-3 space-y-2.5" data-testid="compliance-sprinto-key-form">
          <div className="text-[11.5px] text-muted-foreground">
            {status?.configured
              ? "An API key is saved but hasn't synced yet \u2014 click \u201cImport now\u201d below, or update the key first."
              : "Paste your Sprinto API key to enable import."}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="password"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder="Sprinto API key"
              className="flex-1 min-w-[200px] h-9 px-3 rounded-lg border border-border bg-background text-[12.5px] focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="compliance-sprinto-key-input"
            />
            <select
              value={regionInput}
              onChange={(e) => setRegionInput(e.target.value)}
              className="h-9 px-2 rounded-lg border border-border bg-background text-[12.5px]"
              data-testid="compliance-sprinto-region-select"
            >
              <option value="us">US</option>
              <option value="eu">EU</option>
              <option value="in">IN</option>
            </select>
            <Button
              size="sm"
              className="h-9 gap-1.5"
              disabled={!apiKeyInput.trim() || savingKey}
              onClick={saveKey}
              data-testid="compliance-sprinto-key-save-btn"
            >
              {savingKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
              Save key
            </Button>
          </div>
        </div>
      )}

      {jobRunning && (
        <div className="flex items-center gap-2 text-[12px] text-primary mt-3" data-testid="compliance-sprinto-job-running">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> {job.status === "queued" ? "Queued…" : "Importing from Sprinto…"}
        </div>
      )}

      {!jobRunning && job && (
        <div className="mt-3 space-y-1.5">
          <div className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground" data-testid="compliance-sprinto-last-job">
            <Clock className="w-3 h-3" />
            Last run {job.finished_at ? new Date(job.finished_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
            {" · "}
            <span className={job.status === "succeeded" ? "text-emerald-600" : job.status === "partial" ? "text-amber-600" : "text-red-600"}>{job.status}</span>
          </div>
          {job.error && <div className="text-[11.5px] text-red-600">{job.error}</div>}
          {job.results && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-1">
              {Object.entries(ENTITY_LABELS).map(([key, label]) => {
                const r = job.results[key] || {};
                return (
                  <div
                    key={key}
                    className="rounded-lg border border-border bg-muted/20 px-2 py-1.5"
                    data-testid={`compliance-sprinto-result-${key}`}
                    title={r.error || undefined}
                  >
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                    <div className="text-[13px] font-semibold">{r.found === false ? "—" : r.imported ?? 0}</div>
                    {r.found === false && (
                      <div className="text-[9.5px] text-muted-foreground truncate" title={r.error}>
                        {r.unsupported ? "not in API plan" : "not found · hover for details"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="flex items-center gap-2 mt-3.5">
          <Button
            size="sm"
            className="h-8 gap-1.5"
            disabled={!status?.configured || jobRunning || starting}
            onClick={() => trigger(syncedBefore ? "resync" : "import")}
            data-testid="compliance-sprinto-import-btn"
          >
            {syncedBefore ? <RefreshCw className="w-3.5 h-3.5" /> : <DownloadCloud className="w-3.5 h-3.5" />}
            {syncedBefore ? "Sync" : "Import now"}
          </Button>
          {totalImported > 0 && <span className="text-[11px] text-muted-foreground">{totalImported} records imported so far</span>}
        </div>
      )}

      {isAdmin && (
        <div className="mt-3 border-t border-border pt-2.5">
          <button
            type="button"
            onClick={() => {
              const next = !historyOpen;
              setHistoryOpen(next);
              if (next && !historyLoaded) loadHistory();
            }}
            className="flex items-center gap-1.5 text-[11.5px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            data-testid="compliance-sprinto-history-toggle"
          >
            <History className="w-3.5 h-3.5" /> Sync history
            {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {historyOpen && (
            <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto tabler-scroll" data-testid="compliance-sprinto-history-list">
              {historyLoading ? (
                <div className="h-16 rounded-lg bg-muted/30 animate-pulse" />
              ) : historyJobs.length === 0 ? (
                <div className="text-[11.5px] text-muted-foreground py-2">No sync attempts yet.</div>
              ) : historyJobs.map((j) => {
                const entries = Object.entries(j.results || {});
                const imported = entries.reduce((sum, [, r]) => sum + (r.imported || 0), 0);
                const errored = entries.filter(([, r]) => r.error && !r.unsupported).length;
                const unsupportedCount = entries.filter(([, r]) => r.unsupported).length;
                const StatusIcon = j.status === "succeeded" ? CheckCircle2 : j.status === "partial" ? AlertTriangle : j.status === "failed" ? XCircle : Loader2;
                const statusColor = j.status === "succeeded" ? "text-emerald-600" : j.status === "partial" ? "text-amber-600" : j.status === "failed" ? "text-red-600" : "text-sky-600";
                const isExpanded = expandedJobId === j.id;
                return (
                  <div key={j.id} className="rounded-lg border border-border bg-muted/15" data-testid={`compliance-sprinto-history-row-${j.id}`}>
                    <button
                      type="button"
                      onClick={() => setExpandedJobId(isExpanded ? null : j.id)}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 text-left"
                      data-testid={`compliance-sprinto-history-row-toggle-${j.id}`}
                    >
                      <StatusIcon className={`w-3.5 h-3.5 shrink-0 ${statusColor} ${["queued", "running"].includes(j.status) ? "animate-spin" : ""}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-medium flex items-center gap-1.5">
                          <span className={statusColor}>{j.status}</span>
                          <span className="text-muted-foreground">·</span>
                          <span className="text-muted-foreground">{imported} imported</span>
                          {errored > 0 && <span className="text-red-600">· {errored} entity error{errored > 1 ? "s" : ""}</span>}
                          {unsupportedCount > 0 && <span className="text-muted-foreground">· {unsupportedCount} not in API plan</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {j.finished_at ? fmtMDT(j.finished_at) : j.started_at ? fmtMDT(j.started_at) : "—"}
                          {j.triggered_by && <> · by {j.triggered_by}</>}
                        </div>
                        {j.error && <div className="text-[11px] text-red-600 truncate" title={j.error}>{j.error}</div>}
                      </div>
                      {entries.length > 0 && (isExpanded ? <ChevronUp className="w-3.5 h-3.5 shrink-0 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />)}
                    </button>
                    {isExpanded && entries.length > 0 && (
                      <div className="px-2.5 pb-2.5 pt-0.5 space-y-1" data-testid={`compliance-sprinto-history-detail-${j.id}`}>
                        {entries.map(([key, r]) => (
                          <div key={key} className="rounded-md bg-background/60 border border-border/60 px-2 py-1.5">
                            <div className="text-[11.5px] font-medium flex items-center gap-1.5">
                              <span>{ENTITY_LABELS[key] || key}</span>
                              <span className="text-muted-foreground">·</span>
                              <span className={r.found === false ? "text-muted-foreground" : "text-foreground"}>{r.found === false ? (r.unsupported ? "not in API plan" : "not found") : `${r.imported ?? 0} imported`}</span>
                            </div>
                            {r.error && <div className={`text-[11px] mt-0.5 break-words ${r.unsupported ? "text-muted-foreground" : "text-red-600"}`}>{r.error}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ComplianceIntegrations({ isAdmin }) {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/compliance/integrations").then((r) => setIntegrations(r.data.integrations || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <SprintoImportCard isAdmin={isAdmin} />
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{[0, 1, 2].map((i) => <div key={i} className="h-28 rounded-xl bg-muted/40 animate-pulse" />)}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="compliance-integrations-grid">
          {integrations.map((i) => {
            const Icon = ICONS[i.icon] || ShieldCheck;
            return (
              <div key={i.key} className="rounded-xl border border-border bg-card p-4" data-testid={`compliance-integration-${i.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <Icon className="w-4.5 h-4.5 text-primary" />
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 px-2 py-0.5">
                    <CheckCircle2 className="w-3 h-3" /> Connected
                  </span>
                </div>
                <div className="text-[13.5px] font-semibold mt-2.5">{i.name}</div>
                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{i.subtitle}</div>
                <div className="text-[11.5px] text-muted-foreground mt-1.5">{i.description}</div>
                {i.sample_mode && <div className="text-[10.5px] text-amber-600 mt-2 font-medium">Sample mode — fresh seed data</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
